import {
	Alert,
	Badge,
	Group,
	Loader,
	Paper,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { portalAccessKey } from "@/features/account/lib/portal-access";
import type { StaffAccount } from "@/features/auth/api/auth-staff";
import type { AuthSession } from "@/features/auth/lib/auth-store";
import { describeApiErrorSync } from "@/shared/api/error-message";

export function ProfilePanel({
	account,
	session,
	isPortalAdmin,
	canWorkRequests,
	loading,
	error,
}: {
	account?: StaffAccount | null;
	session?: AuthSession | null;
	isPortalAdmin: boolean;
	canWorkRequests: boolean;
	loading: boolean;
	error: unknown;
}) {
	const { t } = useTranslation();
	const tags = account?.tags ?? session?.tags ?? [];
	const accessKey = portalAccessKey({ isPortalAdmin, canWorkRequests, tags });

	return (
		<Paper p="md" radius="lg" withBorder h="100%">
			{loading ? (
				<Group justify="center" py="xl">
					<Loader />
				</Group>
			) : null}
			{error ? (
				<Alert color="red" title={t("common.loadError")}>
					{describeApiErrorSync(error)}
				</Alert>
			) : null}
			{account || session ? (
				<Stack gap="sm">
					<Title order={4}>{t("staff.profileTitle")}</Title>
					<div>
						<Text size="sm" c="dimmed">
							{t("common.email")}
						</Text>
						<Text fw={500}>{account?.email ?? session?.email}</Text>
					</div>
					<div>
						<Text size="sm" c="dimmed">
							{t("common.name")}
						</Text>
						<Text fw={500}>{account?.fullName ?? session?.fullName}</Text>
					</div>
					<div>
						<Text size="sm" c="dimmed">
							{t("staff.role")}
						</Text>
						<Badge variant="light">{account?.role ?? session?.role}</Badge>
					</div>
					<div>
						<Text size="sm" c="dimmed" mb={4}>
							{t("staff.tags")}
						</Text>
						{tags.length === 0 ? (
							<Text size="sm" c="dimmed">
								{isPortalAdmin
									? t("staff.tagsEmptyAdmin")
									: t("staff.tagsEmptyStaff")}
							</Text>
						) : (
							<Group gap={4}>
								{tags.map((tag) => (
									<Badge key={tag} variant="outline">
										{tag}
									</Badge>
								))}
							</Group>
						)}
					</div>
					<div>
						<Text size="sm" c="dimmed">
							{t("staff.access")}
						</Text>
						<Text size="sm">{t(`staff.${accessKey}`)}</Text>
					</div>
				</Stack>
			) : null}
		</Paper>
	);
}
